import { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Button, Input, Chip, Tooltip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Select, SelectItem, Textarea } from "@heroui/react";
import { Plus, CheckCircle, Trash, PencilSimple, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";

interface CorrectionFactor {
  id: number;
  epcLabel: string;
  anniversaryMonth: number;
  value: number;
  formula?: string;
  referenceYear1?: number;
  referenceYear2?: number;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const EPC_LABELS = ["A+", "A", "B", "C", "D", "E", "F", "NONE"];

const MONTH_NAMES_NL = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

const monthLabel = (month: number): string =>
  MONTH_NAMES_NL[month - 1] || `Maand ${month}`;

const CorrectionFactorsTab = () => {
  const { session } = useSession();
  const [factors, setFactors] = useState<CorrectionFactor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Add/Edit modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingFactor, setEditingFactor] = useState<CorrectionFactor | null>(null);
  const [formData, setFormData] = useState({
    epcLabel: "D",
    anniversaryMonth: "3",
    value: "",
    formula: "",
    referenceYear1: "",
    referenceYear2: "",
    notes: "",
    isActive: true,
  });

  // Load data on mount
  useEffect(() => {
    fetchFactors();
  }, [session]);

  // Fetch all correction factors
  const fetchFactors = async () => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/admin/correction-factors`,
        { method: "GET" },
        session
      );

      if (!response.ok) throw new Error("Failed to fetch correction factors");

      const data = await response.json();
      setFactors(data);
    } catch (error) {
      console.error("Error fetching correction factors:", error);
      toast.error("Fout bij ophalen van correctiefactoren");
    } finally {
      setIsLoading(false);
    }
  };

  // Open add modal
  const handleAddNew = () => {
    setEditingFactor(null);
    setFormData({
      epcLabel: "D",
      anniversaryMonth: "3",
      value: "",
      formula: "",
      referenceYear1: "",
      referenceYear2: "",
      notes: "",
      isActive: true,
    });
    setIsAddModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (factor: CorrectionFactor) => {
    setEditingFactor(factor);
    setFormData({
      epcLabel: factor.epcLabel,
      anniversaryMonth: factor.anniversaryMonth.toString(),
      value: factor.value.toString(),
      formula: factor.formula || "",
      referenceYear1: factor.referenceYear1?.toString() || "",
      referenceYear2: factor.referenceYear2?.toString() || "",
      notes: factor.notes || "",
      isActive: factor.isActive,
    });
    setIsAddModalOpen(true);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!session) return;

    const value = parseFloat(formData.value);
    if (isNaN(value) || value <= 0 || value > 2) {
      toast.error("Ongeldige waarde. Moet tussen 0 en 2 zijn.");
      return;
    }

    const anniversaryMonth = parseInt(formData.anniversaryMonth, 10);
    if (isNaN(anniversaryMonth) || anniversaryMonth < 1 || anniversaryMonth > 12) {
      toast.error("Ongeldige verjaardagsmaand. Moet tussen 1 en 12 zijn.");
      return;
    }

    try {
      const payload: any = {
        value,
        formula: formData.formula || undefined,
        notes: formData.notes || undefined,
        isActive: formData.isActive,
      };

      if (formData.referenceYear1) {
        payload.referenceYear1 = parseInt(formData.referenceYear1);
      }

      if (formData.referenceYear2) {
        payload.referenceYear2 = parseInt(formData.referenceYear2);
      }

      if (editingFactor) {
        // Update existing
        const response = await fetchWithAuth(
          `${API_URL}/rental/admin/correction-factors/${editingFactor.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
          session
        );

        if (!response.ok) throw new Error("Failed to update");
        toast.success("Correctiefactor bijgewerkt");
      } else {
        // Create new
        payload.epcLabel = formData.epcLabel;
        payload.anniversaryMonth = anniversaryMonth;

        const response = await fetchWithAuth(
          `${API_URL}/rental/admin/correction-factors`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
          session
        );

        if (!response.ok) throw new Error("Failed to create");
        toast.success("Correctiefactor toegevoegd");
      }

      setIsAddModalOpen(false);
      fetchFactors();
    } catch (error) {
      console.error("Error saving correction factor:", error);
      toast.error("Fout bij opslaan");
    }
  };

  // Delete
  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!confirm("Weet je zeker dat je deze correctiefactor wilt verwijderen?")) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/admin/correction-factors/${id}`,
        { method: "DELETE" },
        session
      );

      if (!response.ok) throw new Error("Failed to delete");

      toast.success("Correctiefactor verwijderd");
      fetchFactors();
    } catch (error) {
      console.error("Error deleting correction factor:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  // Filter factors by search term
  const filteredFactors = factors.filter(factor => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      factor.epcLabel.toLowerCase().includes(search) ||
      monthLabel(factor.anniversaryMonth).toLowerCase().includes(search) ||
      factor.value.toString().includes(search) ||
      factor.formula?.toLowerCase().includes(search) ||
      factor.notes?.toLowerCase().includes(search)
    );
  });

  // Sort: EPC label then anniversary month
  const sortedFactors = [...filteredFactors].sort((a, b) => {
    if (a.epcLabel !== b.epcLabel) return a.epcLabel.localeCompare(b.epcLabel);
    return a.anniversaryMonth - b.anniversaryMonth;
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Info Card */}
      <Card className="border-2 border-moss-primary/30 bg-moss-secondaryFine/30">
        <CardBody className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={24} weight="fill" className="text-moss-primary mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-moss-primary mb-1">
                EPC Correctiefactoren Beheer
              </div>
              <div className="text-sm text-neutral-secondary-mid">
                Beheer EPC-gebaseerde correctiefactoren voor huurprijsindexatie volgens het Vlaams Woninghuurdecreet.
                Statbel publiceert per verjaardagsmaand (januari t/m december) een aparte correctiefactor per EPC-label.
                Deze factoren worden toegepast op huurcontracten met EPC label D, E, F of zonder EPC, die in werking
                getreden zijn vóór 1 oktober 2022.{" "}
                <strong>Referentie: Vlaamse Regering 2022-2023</strong>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Warning Card */}
      <Card className="border-2 border-amber-500/30 bg-amber-50/30">
        <CardBody className="p-4">
          <div className="flex items-start gap-3">
            <Warning size={24} weight="fill" className="text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-700 mb-1">
                Let op: Berekeningsprioriteit
              </div>
              <div className="text-sm text-neutral-secondary-mid">
                Het systeem gebruikt eerst de waarden uit deze database. Als geen waarde gevonden wordt,
                valt het systeem terug op berekening op basis van de gezondheidsindex. Wijzigingen hier
                beïnvloeden direct alle toekomstige indexatieberekeningen.
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Input
            placeholder="Zoek op EPC label, maand, waarde..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            startContent={<MagnifyingGlass size={18} className="text-neutral-secondary-mid" />}
            classNames={{
              base: "max-w-md",
              input: "text-sm",
            }}
            size="sm"
          />
        </div>

        <Button
          size="sm"
          color="primary"
          startContent={<Plus size={16} weight="bold" />}
          onPress={handleAddNew}
        >
          Nieuwe Factor
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="bg-neutral-secondaryFine px-4 py-3">
          <div className="text-base font-semibold">Correctiefactoren</div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-secondary-mid">Laden...</div>
            </div>
          ) : sortedFactors.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-secondary-mid">Geen data gevonden</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-secondaryFine border-b border-neutral-secondaryFine">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      EPC Label
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Verjaardagsmaand
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Correctiefactor
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Formule
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Referentiejaren
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Status
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-secondary-mid">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFactors.map((factor, i) => (
                    <tr
                      key={factor.id}
                      className={`border-b border-neutral-secondaryFine hover:bg-neutral-secondaryFine/50 ${
                        i % 2 === 0 ? "bg-white" : "bg-neutral-secondaryFine/20"
                      }`}
                    >
                      <td className="px-4 py-3">
                        <Chip
                          size="sm"
                          className={`font-semibold ${
                            ["A+", "A", "B", "C"].includes(factor.epcLabel)
                              ? "bg-status-good/20 text-status-good border border-status-good/30"
                              : factor.epcLabel === "D"
                              ? "bg-amber-100 text-amber-700 border border-amber-300"
                              : "bg-brick-secondary-fine/50 text-brick-primary border border-brick-primary/30"
                          }`}
                        >
                          EPC {factor.epcLabel}
                        </Chip>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">
                        {monthLabel(factor.anniversaryMonth)}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono font-bold">
                        {Number(factor.value).toFixed(9)}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-secondary-mid max-w-xs truncate">
                        {factor.formula || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-secondary-mid">
                        {factor.referenceYear1 && factor.referenceYear2
                          ? `${factor.referenceYear1} / ${factor.referenceYear2}`
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <Chip
                          size="sm"
                          className={
                            factor.isActive
                              ? "bg-moss-secondaryFine text-moss-primary border border-moss-primary/30"
                              : "bg-neutral-secondaryFine text-neutral-secondary-mid border border-neutral-secondary-mid/30"
                          }
                        >
                          {factor.isActive ? "Actief" : "Inactief"}
                        </Chip>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip content="Bewerken">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() => handleEdit(factor)}
                            >
                              <PencilSimple size={16} />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Verwijderen">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              color="danger"
                              onPress={() => handleDelete(factor.id)}
                            >
                              <Trash size={16} />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} size="2xl">
        <ModalContent>
          <ModalHeader>
            {editingFactor ? "Correctiefactor bewerken" : "Nieuwe correctiefactor toevoegen"}
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="EPC Label"
                  selectedKeys={[formData.epcLabel]}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0] as string;
                    setFormData({ ...formData, epcLabel: key });
                  }}
                  isDisabled={!!editingFactor}
                >
                  {EPC_LABELS.map((label) => (
                    <SelectItem key={label} textValue={label}>
                      {label === "NONE" ? "Geen EPC" : `EPC ${label}`}
                    </SelectItem>
                  ))}
                </Select>

                <Select
                  label="Verjaardagsmaand"
                  selectedKeys={[formData.anniversaryMonth]}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0] as string;
                    setFormData({ ...formData, anniversaryMonth: key });
                  }}
                  isDisabled={!!editingFactor}
                >
                  {MONTH_NAMES_NL.map((name, idx) => {
                    const value = (idx + 1).toString();
                    return (
                      <SelectItem key={value} textValue={name}>
                        {name.charAt(0).toUpperCase() + name.slice(1)}
                      </SelectItem>
                    );
                  })}
                </Select>
              </div>

              <Input
                label="Correctiefactor Waarde"
                type="number"
                step="0.000000001"
                value={formData.value}
                onValueChange={(v) => setFormData({ ...formData, value: v })}
                description="Waarde tussen 0 en 2 (bijv. 0.967996216 of 1.0)"
                isRequired
              />

              <Textarea
                label="Formule (optioneel)"
                value={formData.formula}
                onValueChange={(v) => setFormData({ ...formData, formula: v })}
                description="Beschrijving van de formule"
                placeholder="bijv. GI 2022 / GI 2023"
                rows={2}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Referentiejaar 1 (optioneel)"
                  type="number"
                  value={formData.referenceYear1}
                  onValueChange={(v) => setFormData({ ...formData, referenceYear1: v })}
                  placeholder="bijv. 2022"
                />
                <Input
                  label="Referentiejaar 2 (optioneel)"
                  type="number"
                  value={formData.referenceYear2}
                  onValueChange={(v) => setFormData({ ...formData, referenceYear2: v })}
                  placeholder="bijv. 2023"
                />
              </div>

              <Textarea
                label="Notities (optioneel)"
                value={formData.notes}
                onValueChange={(v) => setFormData({ ...formData, notes: v })}
                description="Aanvullende informatie of context"
                placeholder="bijv. Statbel gepubliceerde waarde voor maart-verjaardag"
                rows={3}
              />

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm">Actief (gebruik deze factor bij berekeningen)</label>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsAddModalOpen(false)}>
              Annuleren
            </Button>
            <Button color="primary" onPress={handleSave}>
              {editingFactor ? "Bijwerken" : "Toevoegen"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CorrectionFactorsTab;
